// src/persistence/packets.ts
import { getAll, getOne, putOne, deleteOne } from "./db";
import { mergeNotes, MergeReport } from "./merge";
import { CueNote } from "../types";
import { NOTES_KEY } from "../state/notes-store";
import { buildTriggersForNotes } from "../lib/trigger-builder"; // ← keep

export type Packet = {
  id: string;
  name: string;
  notes: CueNote[];
  createdAt: string;
  updatedAt: string;
  version: "v1";
};

const META_KEY_CURRENT = "currentPacketId";

/** ---------- Trigger dedupe + note normalization ---------- */
const dedupeTriggers = (triggers?: string[] | null): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of Array.isArray(triggers) ? triggers : []) {
    const t = (raw ?? "").trim();
    if (!t) continue;
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
};

const normalizeNote = (n: CueNote): CueNote => ({
  ...n,
  id: n.id && n.id.trim() ? n.id : crypto.randomUUID(),
  title: typeof n.title === "string" ? n.title.trim() : "",
  body_markdown: typeof n.body_markdown === "string" ? n.body_markdown : "",
  triggers: dedupeTriggers(n.triggers),
});

const withIds = (notes: CueNote[]): CueNote[] =>
  (notes || []).map(n => ({ ...n, id: n.id || crypto.randomUUID() }));

const normalizeNotes = (arr: CueNote[]): CueNote[] =>
  (Array.isArray(arr) ? arr : []).map(normalizeNote);

/** ---------- Helpers ---------- */
async function ensureUniquePacketName(base: string): Promise<string> {
  const packets = await getAll<Packet>("packets");
  const existing = new Set(packets.map(p => (p.name ?? "").trim().toLowerCase()));

  const trim = (s: string) => (s ?? "").trim();
  const baseTrim = trim(base);
  if (!existing.has(baseTrim.toLowerCase())) return baseTrim || "Untitled Packet";

  const suffixRe = /(.*)\s\((\d+)\)$/;
  const m = baseTrim.match(suffixRe);
  let stem = baseTrim;
  let start = 2;

  if (m) {
    stem = trim(m[1]);
    const n = parseInt(m[2], 10);
    if (!Number.isNaN(n) && n >= 2) start = n + 1;
  }

  let candidate = "";
  let i = start;
  while (true) {
    candidate = `${stem} (${i})`;
    if (!existing.has(candidate.toLowerCase())) return candidate;
    i++;
    if (i > 9999) return `${stem} (${Date.now()})`;
  }
}

/** ---------- Basics ---------- */
export async function listPackets(): Promise<Packet[]> {
  return getAll<Packet>("packets");
}

export async function getPacket(id: string): Promise<Packet | undefined> {
  return getOne<Packet>("packets", id);
}

export async function createPacket(name: string, notes: CueNote[]): Promise<Packet> {
  const uniqueName = await ensureUniquePacketName(name && name.trim() ? name : "Untitled Packet");

  const now = new Date().toISOString();
  const p: Packet = {
    id: crypto.randomUUID(),
    name: uniqueName,
    notes: normalizeNotes(withIds(notes)),
    createdAt: now,
    updatedAt: now,
    version: "v1",
  };
  await putOne("packets", p);
  return p;
}

export async function renamePacket(id: string, name: string): Promise<Packet | undefined> {
  const ex = await getPacket(id);
  if (!ex) return undefined;
  ex.name = (name ?? "").trim();
  ex.updatedAt = new Date().toISOString();
  await putOne("packets", ex);
  return ex;
}

export async function deletePacketById(id: string): Promise<void> {
  await deleteOne("packets", id);
  const cur = await getCurrentPacketId();
  if (cur === id) {
    await setCurrentPacket(null);
    localStorage.removeItem(NOTES_KEY);
  }
}

export async function setCurrentPacket(id: string | null): Promise<void> {
  await putOne("meta", { key: META_KEY_CURRENT, value: id });
  if (id) {
    const p = await getPacket(id);
    if (p) {
      try {
        localStorage.setItem(NOTES_KEY, JSON.stringify(normalizeNotes(p.notes)));
      } catch {}
    }
  }
}

export async function getCurrentPacketId(): Promise<string | null> {
  const row = await getOne<{ key: string; value: string | null }>("meta", META_KEY_CURRENT);
  return (row?.value ?? null) as string | null;
}

export async function updatePacketNotes(
  id: string,
  newNotes?: CueNote[],
  opts?: { mirrorOnly?: boolean }
): Promise<Packet | undefined> {
  const p = await getPacket(id);
  if (!p) return undefined;

  if (!opts?.mirrorOnly && newNotes) {
    p.notes = normalizeNotes(withIds(newNotes));
    p.updatedAt = new Date().toISOString();
    await putOne("packets", p);
  }

  try {
    localStorage.setItem(NOTES_KEY, JSON.stringify(normalizeNotes(p.notes)));
  } catch {}

  return p;
}

export async function exportPacket(id: string): Promise<any> {
  const p = await getPacket(id);
  if (!p) return null;
  return {
    cueboardPacket: 1,
    name: p.name,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
    version: p.version,
    notes: normalizeNotes(p.notes),
  };
}

/**
 * Import packets (merge or new).
 */
export async function importPacket(
  json: any,
  opts: { merge?: boolean; autoTriggers?: boolean } = { merge: true }
): Promise<{ packet: Packet; report?: MergeReport; summary?: { notes: number; triggersKept: number; triggersDropped: number } }> {
  let name = "Imported Packet";
  let incoming: CueNote[] = [];

  if (Array.isArray(json)) {
    incoming = json as CueNote[];
  } else if (json && typeof json === "object") {
    if (Array.isArray(json.notes)) {
      incoming = json.notes as CueNote[];
      if (typeof json.name === "string") name = json.name;
    } else if (json.cueboardPacket === 1 && Array.isArray(json.notes)) {
      incoming = json.notes as CueNote[];
      if (typeof json.name === "string") name = json.name;
    }
  }

  const normalizedIncoming = normalizeNotes(withIds(incoming));
  const origTriggerCount = normalizedIncoming.reduce((acc, n) => acc + (Array.isArray(n.triggers) ? n.triggers.length : 0), 0);
  const merge = opts?.merge !== false;
  const incomingFinal = opts?.autoTriggers
    ? buildTriggersForNotes(normalizedIncoming)
    : normalizedIncoming;
  const finalTriggerCount = incomingFinal.reduce((acc, n) => acc + (Array.isArray(n.triggers) ? n.triggers.length : 0), 0);

  const summary = {
    notes: normalizedIncoming.length,
    triggersKept: finalTriggerCount,
    triggersDropped: Math.max(0, origTriggerCount - finalTriggerCount),
  };

  const currentId = await getCurrentPacketId();

  // CASE A: Merge into current packet
  if (merge && currentId) {
    const current = await getPacket(currentId);
    if (current) {
      const { merged, report } = mergeNotes(current.notes, incomingFinal);
      const updated = await updatePacketNotes(current.id, merged);
      if (updated) {
        await setCurrentPacket(updated.id);
        return { packet: updated, report, summary };
      }
    }
  }

  // CASE B: Create new packet
  const newPacketNotes =
    !merge && opts?.autoTriggers
      ? buildTriggersForNotes(incomingFinal)
      : incomingFinal;

  const fresh = await createPacket(name, newPacketNotes);
  await setCurrentPacket(fresh.id);

  if (merge && !currentId) {
    return {
      packet: fresh,
      report: { added: normalizedIncoming.length, updated: 0, skipped: 0, conflicts: 0 },
      summary,
    };
  }

  return { packet: fresh, summary };
}
